import React, { useEffect, useState } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
interface StatCardProps {
  percentage?: number;
  prefix?: string;
  value: string;
  description: string;
  delay: number;
}
const StatCard = ({
  percentage,
  prefix,
  value,
  description,
  delay
}: StatCardProps) => {
  const [count, setCount] = useState(0);
  const controls = useAnimation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.2
  });
  useEffect(() => {
    if (inView) {
      controls.start({
        opacity: 1,
        y: 0,
        transition: {
          duration: 0.7,
          delay
        }
      });
      if (percentage) {
        let startTimestamp: number;
        const duration = 2000;
        const step = (timestamp: number) => {
          if (!startTimestamp) startTimestamp = timestamp;
          const progress = Math.min((timestamp - startTimestamp) / duration, 1);
          setCount(Math.floor(progress * percentage));
          if (progress < 1) {
            window.requestAnimationFrame(step);
          }
        };
        window.requestAnimationFrame(step);
      }
    }
  }, [inView, controls, percentage, delay]);
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 50
  }} animate={controls} className="w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1rem)] mb-6" data-id="element-149">
      <div className="h-full bg-gradient-to-b from-[#111111] to-[#0a0a0a] border border-gray-800 rounded-lg p-6 shadow-xl flex flex-col justify-between" data-id="element-150">
        <div data-id="element-151">
          <h3 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-152">
            {prefix && <span className="text-[#ffdf07]" data-id="element-153">{prefix}</span>}
            {percentage && <span className="text-[#ffdf07]" data-id="element-154">
                {count}
                <span data-id="element-155">%</span>
              </span>}
            {!percentage && <span className="text-[#ffdf07]" data-id="element-156">{value}</span>}
          </h3>
          <p className="text-gray-300 libre-caslon-text-regular leading-relaxed" data-id="element-157">
            {description}
          </p>
        </div>
      </div>
    </motion.div>;
};
const StatsSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const controls = useAnimation();
  useEffect(() => {
    if (inView) {
      controls.start({
        opacity: 1,
        y: 0,
        transition: {
          duration: 0.7
        }
      });
    }
  }, [inView, controls]);
  return <section className="w-full bg-black py-20 px-4" data-id="element-158">
      <div className="max-w-5xl mx-auto" data-id="element-159">
        <motion.div ref={ref} initial={{
        opacity: 0,
        y: 20
      }} animate={controls} className="text-center mb-14" data-id="element-160">
          <h2 className="text-white text-3xl md:text-4xl mb-6 typekit-heading" data-id="element-161">
            The Hard Numbers: Why This Matters
          </h2>
        </motion.div>
        <div className="flex flex-wrap justify-center gap-6" data-id="element-162">
          <StatCard percentage={98} description="of small business owners in your revenue range will never exit for what their business is truly worth." delay={0.1} data-id="element-163" />
          <StatCard percentage={82} description="of owners fly blind into a sale without a professional valuation." delay={0.2} data-id="element-164" />
          <StatCard percentage={72} description="of owners leave six or seven figures on the table because they are unprepared." delay={0.3} data-id="element-165" />
          <StatCard percentage={80} description="of healthcare deals that fail are due to poorly organized financials." delay={0.4} data-id="element-166" />
          <StatCard prefix="" value="$700,000+" description="potential loss on a $5M sale from a simple 5% revenue leak." delay={0.5} data-id="element-167" />
        </div>
      </div>
    </section>;
};
export default StatsSection;